import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Input,
  Pagination,
  Select,
  Table,
  type Column,
  type SelectOption,
} from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import {
  researchDbService,
  type FilterOperator,
  type TableColumn,
} from "../../../api/researchDb.service.js";
import styles from "./ResearchWpMappings.module.css";

const PAGE_SIZE = 50;
const DEFAULT_TABLE = "research_wp_mapping";

const OPERATOR_OPTIONS: SelectOption[] = [
  { value: "eq", label: "= (equals)" },
  { value: "neq", label: "!= (not equal)" },
  { value: "contains", label: "contains" },
  { value: "starts_with", label: "starts with" },
  { value: "ends_with", label: "ends with" },
  { value: "gt", label: "> (greater)" },
  { value: "lt", label: "< (less)" },
  { value: "gte", label: ">= (greater or equal)" },
  { value: "lte", label: "<= (less or equal)" },
  { value: "is_null", label: "IS NULL" },
  { value: "is_not_null", label: "IS NOT NULL" },
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type TableRow = { id: string | number } & Record<string, unknown>;

function buildColumns(cols: TableColumn[]): Column<TableRow>[] {
  return cols.map(col => ({
    header: col.name,
    accessor: col.name as keyof TableRow,
    render: (value: unknown) => {
      if (value === null || value === undefined) {
        return <span className={styles.nullValue}>null</span>;
      }
      if (typeof value === "object") {
        return (
          <span className={styles.mono} title={JSON.stringify(value)}>
            {JSON.stringify(value)}
          </span>
        );
      }
      const str = String(value);
      if (col.type.startsWith("timestamp") || ISO_DATE_RE.test(str)) {
        return formatDate(str);
      }
      if (col.type === "uuid" || UUID_RE.test(str)) {
        return <span className={styles.mono}>{str}</span>;
      }
      return <span title={str}>{str}</span>;
    },
  }));
}

function ResearchDbPage() {
  const [tables, setTables] = useState<string[]>([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterColumn, setFilterColumn] = useState("");
  const [filterOp, setFilterOp] = useState<FilterOperator>("contains");
  const [filterValue, setFilterValue] = useState("");

  const fetchRows = useCallback(
    async (
      tableName: string,
      pageOffset: number,
      filter: { column?: string; op?: FilterOperator; value?: string },
    ) => {
      if (!tableName) return;
      setIsLoading(true);
      setError(null);
      try {
        const result = await researchDbService.getRows(tableName, {
          limit: PAGE_SIZE,
          offset: pageOffset,
          filterColumn: filter.column || undefined,
          filterOp: filter.column ? filter.op || "contains" : undefined,
          filterValue: filter.value || undefined,
        });
        setColumns(result.columns);
        setRows(result.rows);
        setTotal(result.pagination.total);
        setOffset(result.pagination.offset);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    researchDbService
      .listTables()
      .then(result => {
        setTables(result.tables);
        const initial = result.tables.includes(DEFAULT_TABLE)
          ? DEFAULT_TABLE
          : (result.tables[0] ?? "");
        if (initial) {
          setSelectedTable(initial);
          fetchRows(initial, 0, {});
        }
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, [fetchRows]);

  const handleTableChange = (tableName: string) => {
    setSelectedTable(tableName);
    setFilterColumn("");
    setFilterValue("");
    setOffset(0);
    setColumns([]);
    setRows([]);
    fetchRows(tableName, 0, {});
  };

  const handleApplyFilter = () => {
    setOffset(0);
    fetchRows(selectedTable, 0, {
      column: filterColumn || undefined,
      op: filterOp,
      value: filterValue || undefined,
    });
  };

  const handleClearFilter = () => {
    setFilterColumn("");
    setFilterValue("");
    setOffset(0);
    fetchRows(selectedTable, 0, {});
  };

  const handleFilterKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleApplyFilter();
  };

  const hideValueInput = filterOp === "is_null" || filterOp === "is_not_null";
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const tableOptions: SelectOption[] = tables.map(t => ({
    value: t,
    label: t,
  }));

  const columnOptions: SelectOption[] = [
    { value: "", label: "-- column --" },
    ...columns.map(col => ({ value: col.name, label: col.name })),
  ];

  const tableData: TableRow[] = rows.map((row, idx) => ({
    ...row,
    id: (row.id as string) ?? idx,
  }));

  const tableColumns = buildColumns(columns);

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Research DB Viewer" subtitle="Browse tables in the research schema" />

      <div className={styles.content}>
        <div className={styles.toolbar}>
          <Select
            label="Table"
            options={tableOptions}
            value={selectedTable}
            onChange={e => handleTableChange(e.target.value)}
            size="small"
            disabled={isLoading || tables.length === 0}
          />
          <Select
            label="Column"
            options={columnOptions}
            value={filterColumn}
            onChange={e => setFilterColumn(e.target.value)}
            size="small"
            disabled={isLoading}
          />
          <Select
            label="Operator"
            options={OPERATOR_OPTIONS}
            value={filterOp}
            onChange={e => setFilterOp(e.target.value as FilterOperator)}
            size="small"
            disabled={isLoading}
          />
          {!hideValueInput && (
            <Input
              label="Value"
              value={filterValue}
              onChange={e => setFilterValue(e.target.value)}
              onKeyDown={handleFilterKeyDown}
              placeholder="Filter value..."
              disabled={isLoading}
            />
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleApplyFilter}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Filter"}
          </Button>
          <Button variant="outlined" size="small" onClick={handleClearFilter} disabled={isLoading}>
            Clear
          </Button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.rowCount}>{isLoading ? "Loading..." : `${total} row(s)`}</div>

        {rows.length === 0 && !isLoading && (
          <div className={styles.empty}>No rows match the current filters.</div>
        )}

        {rows.length > 0 && (
          <>
            <Table
              data={tableData}
              columns={tableColumns}
              size="small"
              stickyHeader
              maxHeight="calc(100vh - 320px)"
            />

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={page => {
                  const newOffset = (page - 1) * PAGE_SIZE;
                  setOffset(newOffset);
                  fetchRows(selectedTable, newOffset, {
                    column: filterColumn || undefined,
                    op: filterOp,
                    value: filterValue || undefined,
                  });
                }}
                variant="simple"
                size="small"
                className={styles.paginationCenter}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ResearchDbPage;
